import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AuthGateDialog from "@/components/AuthGateDialog";

const BUST = Date.now(); // anti-cache : garantit la dernière version du studio après chaque déploiement

export default function Studio() {
  const [params] = useSearchParams();
  const iframeRef = useRef(null);
  const [replayGuardReady, setReplayGuardReady] = useState(false);
  const [gate, setGate] = useState(null); // {reason, project} quand le studio demande une connexion
  const { refreshUser } = useAuth();
  useLayoutEffect(() => {
    // Stop replay before the heavy media document exists, including SPA entry.
    // Product analytics remain enabled; replay stays paused for this SPA session.
    window.posthog?.set_config?.({ disable_session_recording: true });
    window.posthog?.stopSessionRecording?.();
    setReplayGuardReady(true);
  }, []);
  const projectId = params.get("project");
  useLayoutEffect(() => {
    const iframe = iframeRef.current;
    return () => iframe?.contentWindow?.BeatCutLifecycle?.dispose();
  }, [projectId, replayGuardReady]);
  useEffect(() => {
    // le studio (iframe) a son propre curseur V3 : on masque celui du parent
    document.body.classList.add("no-cur");
    // synchronise le fond + theme-color du document parent avec le thème du studio (bandes blanches/noires iOS)
    const sync = () => {
      let t = "dark";
      try { t = localStorage.getItem("bc_theme") || "dark"; } catch {}
      const c = t === "light" ? "#ffffff" : "#000000";
      document.documentElement.style.background = c;
      document.body.style.background = c;
      let m = document.querySelector('meta[name="theme-color"]');
      if (!m) { m = document.createElement("meta"); m.name = "theme-color"; document.head.appendChild(m); }
      m.setAttribute("content", c);
    };
    sync();
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    const iv = setInterval(sync, 1500);
    return () => {
      clearInterval(iv);
      document.body.classList.remove("no-cur");
      document.documentElement.style.background = "";
      document.body.style.background = "";
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, []);
  useEffect(() => {
    // Le studio (session anonyme) demande la connexion au moment de télécharger
    const onMsg = (e) => {
      if (e.origin !== window.location.origin || !e.data || e.data.type !== "bc-login-required") return;
      setGate({ reason: e.data.reason || "export", project: e.data.project || null });
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);
  const sessionId = params.get("session_id");
  const qs = [
    projectId ? `project=${projectId}` : null,
    params.get("new") ? "new=1" : null,
    params.get("pro") ? "pro=1" : null,
    params.get("ws") ? `ws=${encodeURIComponent(params.get("ws"))}` : null,
    params.get("resume") ? `resume=${encodeURIComponent(params.get("resume"))}` : null,
    sessionId ? `session_id=${sessionId}` : null,
    `v=${BUST}`,
  ].filter(Boolean).join("&");
  const afterLoginPath = gate?.project ? `/studio?project=${encodeURIComponent(gate.project)}&resume=${gate.reason}` : "/projects";
  const onLoggedIn = async () => {
    setGate(null);
    try { await refreshUser(); } catch {}
    iframeRef.current?.contentWindow?.postMessage({ type: "bc-logged-in" }, window.location.origin);
  };
  return (
    <div className="w-full" style={{ background: "inherit", position: "fixed", top: "var(--bc-inapp-h, 0px)", left: 0, right: 0, bottom: 0 }}>
      {replayGuardReady && <iframe
        ref={iframeRef}
        src={`/studio.html?${qs}`}
        title="Studio BEATCUT"
        data-testid="studio-iframe"
        className="block w-full h-full border-0"
        allow="autoplay; clipboard-write"
      />}
      <AuthGateDialog open={!!gate} onClose={() => setGate(null)} onLoggedIn={onLoggedIn} afterLoginPath={afterLoginPath} />
    </div>
  );
}
